import type { AdminAiWeeklyReviews } from '@steado/admin-contract/ai-ops';

import { BadgeCell } from '@/components/table/cells';
import { Field, Fields, Panel } from '@/components/panel/panels';
import { formatRate } from '@/lib/format-spend';

import { fallbackRateOf } from './weekly-review-rate';

interface WeeklyReviewPanelProps {
  readonly reviews: AdminAiWeeklyReviews;
  readonly windowDays: number;
}

/**
 * Above this share of model-written prose being thrown away, something has
 * changed: `pnpm eval:weekly-review` measured about 3% on the shipped model.
 */
const FALLBACK_WARN_RATE = 0.1;

/**
 * Who wrote the weekly reviews.
 *
 * The task table already shows a `weekly_review` call that failed at the
 * provider. What it cannot show is a call that succeeded and was discarded
 * because its prose stated a number the week's facts did not contain — the
 * fallback count here is that, and a rising one means the model or the
 * prompt has drifted, not that anyone went without a review.
 */
export function WeeklyReviewPanel({ reviews, windowDays }: WeeklyReviewPanelProps) {
  const fallbackRate = fallbackRateOf(reviews);
  const drifting = fallbackRate !== null && fallbackRate > FALLBACK_WARN_RATE;

  return (
    <Panel
      title="Weekly reviews"
      action={
        <BadgeCell
          label={fallbackRate === null ? 'no model calls' : `${formatRate(fallbackRate)} fell back`}
          tone={drifting ? 'warn' : 'ok'}
          dot
        />
      }
    >
      <div className="flex flex-col gap-3">
        <Fields>
          <Field label={`Model prose, ${String(windowDays)}d`} value={String(reviews.model)} />
          <Field
            label="Fell back to code"
            value={
              drifting ? <span className="text-warn-fg">{reviews.fallback}</span> : String(reviews.fallback)
            }
          />
          <Field label="No AI consent" value={String(reviews.noAiConsent)} />
          <Field label="Nothing measured" value={String(reviews.nothingMeasured)} />
        </Fields>

        {drifting ? (
          <p role="note" className="text-[12px] leading-[1.55] text-warn-fg">
            More than {formatRate(FALLBACK_WARN_RATE)} of the model’s reviews were discarded for breaking a
            prose rule or stating a number the facts do not contain. Every user still got a review; run pnpm
            eval:weekly-review against the configured model before changing anything.
          </p>
        ) : null}

        <p className="text-[12px] leading-[1.55] text-muted-foreground">
          Counts only. The reviews are free on every tier; code-written prose is a complete review, and no
          prose reaches this screen.
        </p>
      </div>
    </Panel>
  );
}
